import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  ProgressBar,
  Divider,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet } from '../api';

interface QuestionResult {
  questionId: string;
  questionText: string;
  questionType: string;
  options: string[];
  optionCounts: Record<string, number>;
  totalResponses: number;
}

interface ResultsData {
  survey: {
    id: string;
    title: string;
    status: string;
    pointsReward: number;
  };
  totalResponses: number;
  questions: QuestionResult[];
}

export default function SurveyResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    apiGet<ResultsData>(`/panel-api/surveys/${id}/results`)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Page title="Survey Results" backAction={{ content: 'Surveys', onAction: () => navigate('/surveys') }}>
        <Card><Spinner size="large" /></Card>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page title="Survey Results" backAction={{ content: 'Surveys', onAction: () => navigate('/surveys') }}>
        <Banner tone="critical"><p>{error || 'Not found'}</p></Banner>
      </Page>
    );
  }

  return (
    <Page
      title={data.survey.title}
      subtitle={`${data.totalResponses} responses`}
      backAction={{ content: 'Surveys', onAction: () => navigate('/surveys') }}
      titleMetadata={
        <Badge tone={data.survey.status === 'active' ? 'success' : undefined}>
          {data.survey.status}
        </Badge>
      }
    >
      <Layout>
        {data.questions.map((q) => (
          <Layout.Section key={q.questionId}>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">{q.questionText}</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {q.questionType === 'single_choice' ? 'Single choice' : 'Multiple choice'}
                  {' · '}{q.totalResponses} responses
                </Text>
                <Divider />
                {q.options.map((option) => {
                  const count = q.optionCounts[option] ?? 0;
                  const percentage = q.totalResponses > 0
                    ? Math.round((count / q.totalResponses) * 100)
                    : 0;

                  return (
                    <BlockStack key={option} gap="100">
                      <InlineStack align="space-between">
                        <Text as="p" variant="bodyMd">{option}</Text>
                        <Text as="p" variant="bodyMd" fontWeight="bold">
                          {count} ({percentage}%)
                        </Text>
                      </InlineStack>
                      <ProgressBar progress={percentage} size="small" />
                    </BlockStack>
                  );
                })}
              </BlockStack>
            </Card>
          </Layout.Section>
        ))}

        {data.questions.length === 0 && (
          <Layout.Section>
            <Card>
              <Text as="p" tone="subdued">No questions in this survey</Text>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
